import React, { useRef, useState } from 'react';
import { equals, isEmpty, map, prop, isNotEmpty } from 'ramda';
import classNames from 'classnames';
import { splitPagesByBreak } from '../docs/components/helpers/pages';
import Layout from '../../layout/default';
import useFetchQuery from '../../hooks/useFetchQuery';
import { QGetDocumentModelsQuery } from '../../_graphql/queries/documents/QGetDocumentModels';
import Loading from '../../components/Loading';
import { QOffersQuery } from '../../_graphql/queries/QOffers';
import withUser from '../../withUser';
import FullscreenLoading from '../../components/FullscreenLoading';
import ModalPreviewDocument from '../docs/components/document/ModalPreviewDocument';
import DuplicateDocumentAndItsWidgets from '../../_graphql/mutations/documents/DuplicateDocumentAndItsWidgets';
import { errorMessage, successMessage } from '../../utils/messageMutation';
import PreviewDocumentHtmlMutation from '../../_graphql/mutations/documents/PreviewDocumentHtmlMutation';
import { getClassNameOrientation } from '../../utils/getClassNameOrientation';
import Card from './components/Card';
import ImportButton from './components/ImportButton';

const Models = ({ userTeam }) => {
  const [fullscreenLoading, setFullscreenLoading] = useState(false);
  const [isModalPreviewVisible, setIsModalPreviewVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const modalDataCache = useRef({});

  const { getDocumentModels: models, isLoading, reFetch } = useFetchQuery({
    query: QGetDocumentModelsQuery,
    dataProp: 'getDocumentModels',
    defaultData: []
  });

  const { offers, isLoading: isLoadingOffers } = useFetchQuery({
    query: QOffersQuery,
    dataProp: 'offers',
    defaultData: [],
    args: { filter: { status: 'draft' } }
  });

  const handlePreview = (cardData) => () => {
    const { orientation, pages, variables, padding, id, renderingEngineVersion, teamId } = cardData;

    setIsModalPreviewVisible(true);

    const existingModalData = prop(id, modalDataCache.current);
    if (existingModalData) {
      setModalData(existingModalData);
      return;
    }

    setIsLoadingHtml(true);

    PreviewDocumentHtmlMutation(
      {
        pages,
        variables: variables || '{}',
        documentId: id,
        padding,
        orientation,
        renderingEngineVersion,
        isVariablesHighlighted: false,
        teamId,
        isModelPreview: true
      },
      (ok, error, html) => {
        setIsLoadingHtml(false);

        if (ok && !error) {
          const modifiedHtml = splitPagesByBreak(html);
          const classNameOrientationDocument = getClassNameOrientation(orientation);

          const newModalData = {
            id,
            teamId,
            pagesHtml: modifiedHtml,
            classNameOrientationDocument
          };

          modalDataCache.current[id] = newModalData;
          setModalData(newModalData);
        } else {
          errorMessage();
        }
      }
    );
  };

  const handleDuplicateDocument = (documentId, teamId) => (targetOffer) => {
    setFullscreenLoading(true);
    DuplicateDocumentAndItsWidgets({
      documentId, offerId: targetOffer, teamId
    }, (ok, error) => {
      if (ok && !error) {
        successMessage('document', 'dupliqué');
        if (equals(prop('teamId', userTeam), teamId)) reFetch();
      } else {
        errorMessage();
      }
      setFullscreenLoading(false);
    });
  };

  const renderDuplicateButton = (onClick, isModal) => {
    const menuItems = map(({ id, offerName, version }) => ({
      key: id,
      label: `${offerName} - ${prop('major', version)}.${prop('minor', version)}`,
      onClick: () => onClick(id)
    }))(offers);

    return <ImportButton menuItems={menuItems} className={classNames({ 'mr-2': isModal })} />;
  };

  return (
    <Layout>
      {(isLoading || isLoadingOffers) ? (
        <Loading />
      ) : (
        <>
          {fullscreenLoading && <FullscreenLoading />}
          {isEmpty(models) ? (
            <span>Aucun modèle trouvé.</span>
          ) : (
            <div className="grid grid-cols-4 gap-8 pt-8">{map((model) => {
              return (
                <div key={model.id} className="bg-flexi-col-table p-5 h-520px rounded-xl">
                  <Card
                    name={prop('name', model)}
                    thumbnail={prop('thumbnail', model)}
                    setIsLoadingHtml={setIsLoadingHtml}
                    handlePreview={handlePreview(model)}
                    duplicateButton={isNotEmpty(offers) && renderDuplicateButton(handleDuplicateDocument(prop('id', model), prop('teamId', model)), false)}
                  />
                </div>
              );
            })(models)}
            </div>
          )}
        </>
      )}
      <ModalPreviewDocument
        hasToBeOnSameSection
        isModel
        isLoading={isLoadingHtml}
        html={prop('pagesHtml', modalData)}
        hide={() => {
          setIsModalPreviewVisible(false);
          setModalData(null);
        }}
        visible={isModalPreviewVisible}
        classNameOrientationDocument={prop('classNameOrientationDocument', modalData)}
        elementsToAdd={isNotEmpty(offers) && renderDuplicateButton(handleDuplicateDocument(prop('id', modalData), prop('teamId', modalData)), true)}
      />
    </Layout>
  );
};

export default withUser(Models);
